import { useState } from 'react'
import { podeFiscalizar } from '../../auth/permissoes'
import { useSessao } from '../../auth/contexto'
import { Explicacao } from '../../components/Explicacao'
import { TEXTOS } from '../../lib/glossario'
import type { LinhaPorPessoa } from '../../types/repositorios'
import { DialogoDeArquivosDaPessoa } from './DialogoDeArquivosDaPessoa'
import { DialogoDeSetor } from './DialogoDeSetor'
import { EtiquetaDeSetor } from './EtiquetaDeSetor'
import { LinhaDaPessoa } from './LinhaDaPessoa'
import { ORDEM_DOS_SETORES, rotuloDoSetor } from './setores'

interface Props {
  linhas: LinhaPorPessoa[]
  competencia: string
}

/**
 * Uma linha por pessoa, agrupadas por setor — o "o que cada servidor fez" que o dono pediu.
 *
 * A ordem dos grupos é a do contrato: a contratada primeiro, robô por último. Cada linha leva à
 * lista de commits daquela pessoa no mês e aos arquivos que ela tocou, e o fiscal pode corrigir o
 * setor quando a regra do e-mail errou — a correção aparece com um selo, para ninguém achar que
 * foi a regra. Quem é da equipe interna não chega aqui: o servidor já o deixou de fora.
 *
 * @param props as linhas e o mês, para o link dos commits
 * @returns as tabelas, uma por setor
 */
export function TabelaPorPessoa({ linhas, competencia }: Props) {
  const t = TEXTOS.repositorios
  const { usuario } = useSessao()
  const [editando, setEditando] = useState<LinhaPorPessoa | null>(null)
  const [vendoArquivos, setVendoArquivos] = useState<LinhaPorPessoa | null>(null)
  const fiscal = podeFiscalizar(usuario)

  const grupos = ORDEM_DOS_SETORES.map((setor) => ({
    setor,
    pessoas: linhas.filter((l) => l.setor === setor),
  })).filter((g) => g.pessoas.length > 0)

  return (
    <div className="space-y-4">
      <p className="flex max-w-prose items-start gap-1 text-sm text-slate-600">
        <span>{t.pessoas.ajuda}</span>
        <Explicacao termo="setorDoAutor" />
      </p>
      {grupos.map((grupo) => (
        <section key={grupo.setor} className="cartao overflow-x-auto" aria-label={rotuloDoSetor(grupo.setor)}>
          <h2 className="flex items-center gap-2 px-4 pt-4 text-base font-semibold text-slate-900">
            <EtiquetaDeSetor setor={grupo.setor} />
            <span className="text-sm font-normal text-slate-500">
              {grupo.pessoas.length} {grupo.pessoas.length === 1 ? 'pessoa' : 'pessoas'}
            </span>
          </h2>
          <table className="mt-2 min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold text-slate-500">
                <th className="px-3 py-2">{t.pessoas.colunas.pessoa}</th>
                <th className="px-3 py-2 text-right">{t.pessoas.colunas.commits}</th>
                <th className="px-3 py-2 text-right">{t.pessoas.colunas.comSolicitacao}</th>
                <th className="px-3 py-2 text-right">{t.pessoas.colunas.integracoes}</th>
                <th className="px-3 py-2 text-right">{t.pessoas.colunas.dias}</th>
                <th className="px-3 py-2 text-right">{t.pessoas.colunas.repositorios}</th>
                <th className="px-3 py-2 text-right">{t.pessoas.colunas.linhas}</th>
                <th className="px-3 py-2">
                  <span className="inline-flex items-center gap-1">
                    {t.pessoas.colunas.areas}
                    <Explicacao termo="areaDoCodigo" />
                  </span>
                </th>
                <th className="px-3 py-2">{t.pessoas.colunas.ultimo}</th>
                <th className="px-3 py-2">
                  <span className="sr-only">{TEXTOS.tabela.acoes}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {grupo.pessoas.map((p) => (
                <LinhaDaPessoa
                  key={p.autorId}
                  pessoa={p}
                  competencia={competencia}
                  fiscal={fiscal}
                  aoCorrigirSetor={() => setEditando(p)}
                  aoVerArquivos={() => setVendoArquivos(p)}
                />
              ))}
            </tbody>
          </table>
        </section>
      ))}
      {editando && (
        <DialogoDeSetor
          autor={{
            id: editando.autorId,
            nome: editando.nome,
            email: editando.email,
            setor: editando.setor,
          }}
          aoFechar={() => setEditando(null)}
        />
      )}
      {vendoArquivos && (
        <DialogoDeArquivosDaPessoa
          competencia={competencia}
          autor={{ id: vendoArquivos.autorId, nome: vendoArquivos.nome }}
          aoFechar={() => setVendoArquivos(null)}
        />
      )}
    </div>
  )
}
